import React from "react";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Edit3, X, Check, Calendar, Clock, PlusCircle, MessageSquare } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUpdateOneOnOneSession } from "@/hooks/api-hooks";

const editOneOnOneSessionSchema = z.object({
  scheduledDate: z.string().min(1, "Please select a date and time"),
  duration: z.number().min(15, "Duration must be at least 15 minutes").max(180, "Duration cannot exceed 180 minutes"),
  agenda: z.array(z.string()).default([]),
  status: z.enum(['scheduled', 'completed', 'cancelled', 'rescheduled', 'missed'], {
    required_error: "Please select a status",
  }),
  notes: z.string().optional(),
  actionItems: z.array(z.string()).default([]),
});

type EditOneOnOneSessionFormData = z.infer<typeof editOneOnOneSessionSchema>;

interface OneOnOneSession {
  id: string;
  setupId: string;
  participantId: string;
  managerId: string;
  scheduledDate: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled' | 'missed';
  duration: number;
  agenda: string[];
  notes?: string;
  actionItems?: string[];
}

interface TeamMember {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  role: 'owner' | 'admin' | 'manager' | 'staff';
}

interface EditOneOnOneSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  session: OneOnOneSession | null;
  teamMembers: TeamMember[];
  onSuccess?: () => void;
}

export function EditOneOnOneSessionModal({
  isOpen,
  onClose,
  teamId,
  session,
  teamMembers,
  onSuccess
}: EditOneOnOneSessionModalProps) {
  const updateSessionMutation = useUpdateOneOnOneSession();
  const [agendaInput, setAgendaInput] = React.useState("");
  const [actionItemInput, setActionItemInput] = React.useState("");

  const normalizeItem = (value: unknown): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'description' in (value as Record<string, unknown>)) {
      const description = (value as Record<string, unknown>).description;
      if (typeof description === 'string') return description;
    }
    return '';
  };

  const mapItems = (items: unknown[] | undefined | null): string[] => {
    if (!Array.isArray(items)) return [];
    return items.map(normalizeItem).filter((item) => item.trim().length > 0);
  };

  const form = useForm<EditOneOnOneSessionFormData>({
    resolver: zodResolver(editOneOnOneSessionSchema),
    defaultValues: {
      scheduledDate: session?.scheduledDate ? new Date(session.scheduledDate).toISOString().slice(0, 16) : "",
      duration: session?.duration || 30,
      agenda: mapItems(session?.agenda),
      status: session?.status || 'scheduled',
      notes: session?.notes || "",
      actionItems: mapItems(session?.actionItems),
    },
  });

  const watchedAgenda = form.watch("agenda");
  const watchedActionItems = form.watch("actionItems");
  const watchedStatus = form.watch("status");

  // Update form when session changes
  React.useEffect(() => {
    if (session) {
      form.reset({
        scheduledDate: new Date(session.scheduledDate).toISOString().slice(0, 16),
        duration: session.duration,
        agenda: mapItems(session.agenda),
        status: session.status,
        notes: session.notes || "",
        actionItems: mapItems(session.actionItems),
      });
    }
  }, [session, form]);

  const handleAddAgendaItem = () => {
    if (agendaInput.trim()) {
      const currentAgenda = form.getValues("agenda");
      form.setValue("agenda", [...currentAgenda, agendaInput.trim()]);
      setAgendaInput("");
    }
  };

  const handleRemoveAgendaItem = (index: number) => {
    const currentAgenda = form.getValues("agenda");
    const newAgenda = currentAgenda.filter((_, i) => i !== index);
    form.setValue("agenda", newAgenda);
  };

  const handleAddActionItem = () => {
    if (actionItemInput.trim()) {
      const currentActionItems = form.getValues("actionItems");
      form.setValue("actionItems", [...currentActionItems, actionItemInput.trim()]);
      setActionItemInput("");
    }
  };

  const handleRemoveActionItem = (index: number) => {
    const currentActionItems = form.getValues("actionItems");
    const newActionItems = currentActionItems.filter((_, i) => i !== index);
    form.setValue("actionItems", newActionItems);
  };

  const onSubmit = async (data: EditOneOnOneSessionFormData) => {
    if (!session) return;

    const submitData = {
      sessionId: session.id,
      teamId,
      ...data,
      status: data.status === 'missed' ? 'cancelled' : data.status,
    };

    updateSessionMutation.mutate(
      submitData,
      {
        onSuccess: () => {
          const participant = teamMembers.find(m => m.user.id === session.participantId);
          const manager = teamMembers.find(m => m.user.id === session.managerId);
          toast.success(`1-on-1 session updated for ${participant?.user.firstName} & ${manager?.user.firstName}!`);

          onSuccess?.();
          onClose();
          setAgendaInput("");
          setActionItemInput("");
        },
        onError: (error: any) => {
          console.error("Error updating 1-on-1 session:", error);
          toast.error(`Failed to update session: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  const handleClose = () => {
    onClose();
    setAgendaInput("");
    setActionItemInput("");
  };

  if (!session) return null;

  const participant = teamMembers.find(m => m.user.id === session.participantId);
  const manager = teamMembers.find(m => m.user.id === session.managerId);

  // Get minimum date (today for rescheduling, or past dates for completion)
  const today = new Date();
  const minDateTime = watchedStatus === 'completed' ? "" : today.toISOString().slice(0, 16);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
              <Edit3 className="h-4 w-4 text-blue-600" />
            </div>
            Edit 1-on-1 Session
          </DialogTitle>
          <div className="text-sm text-muted-foreground">
            {participant?.user.firstName} {participant?.user.lastName} & {manager?.user.firstName} {manager?.user.lastName}
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="scheduledDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date & Time
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        min={minDateTime}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Duration (minutes)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={15}
                        max={180}
                        step={5}
                        placeholder="30"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="scheduled">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-blue-600">Scheduled</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="completed">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-green-600">Completed</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="rescheduled">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-yellow-600">Rescheduled</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="missed">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-amber-600">Missed</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="cancelled">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-red-600">Cancelled</Badge>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Agenda Section */}
            <div className="space-y-4">
              <FormLabel>Agenda Items</FormLabel>
              <div className="flex gap-2">
                <Input
                  placeholder="Add agenda item..."
                  value={agendaInput}
                  onChange={(e) => setAgendaInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAgendaItem();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddAgendaItem}
                  disabled={!agendaInput.trim()}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </div>

              {watchedAgenda.length > 0 && (
                <div className="space-y-2">
                  {watchedAgenda.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                      <span className="flex-1 text-sm">{item}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAgendaItem(index)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes Section */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Notes
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any notes from the session..."
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Action Items Section */}
            <div className="space-y-4">
              <FormLabel>Action Items</FormLabel>
              <div className="flex gap-2">
                <Input
                  placeholder="Add action item..."
                  value={actionItemInput}
                  onChange={(e) => setActionItemInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddActionItem();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddActionItem}
                  disabled={!actionItemInput.trim()}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </div>

              {watchedActionItems.length > 0 && (
                <div className="space-y-2">
                  {watchedActionItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                      <span className="flex-1 text-sm">{item}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveActionItem(index)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={updateSessionMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateSessionMutation.isPending}
                className="gap-2"
              >
                {updateSessionMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-foreground"></div>
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save Changes
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
